/* eslint-disable */
import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import {
  propertiesAPI, paymentsAPI, expensesAPI,
  companyExpensesAPI, prospectsAPI, projectsAPI,
  calendarAPI, settingsAPI, tenantsAPI, companiesAPI
} from '../services/api';

const AppDataContext = createContext(null);

export function AppDataProvider({ children }) {
  const [properties, setProperties] = useState([]);
  const [payments, setPayments] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [companyExpenses, setCompanyExpenses] = useState([]);
  const [prospects, setProspects] = useState([]);
  const [projects, setProjects] = useState([]);
  const [calendarTasks, setCalendarTasks] = useState([]);
  const [tenants, setTenants] = useState([]);
  // COMPANY-003: companies are loaded once alongside everything else. The
  // list is tiny (one row per legal entity) so this adds no meaningful
  // weight to the existing load, unlike the payment/expense history.
  const [companies, setCompanies] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dataError, setDataError] = useState(null);

  const loadAllData = useCallback(async () => {
    setLoading(true);
    setDataError(null);
    try {
      const results = await Promise.allSettled([
        propertiesAPI.getAll(),
        paymentsAPI.getAll(),
        expensesAPI.getAll(),
        companyExpensesAPI.getAll(),
        prospectsAPI.getAll(),
        projectsAPI.getAll(),
        calendarAPI.getAll(),
        settingsAPI.get().catch(() => ({})),
        tenantsAPI.getAll(),
        // COMPANY-003: appended at the END of the array deliberately — the
        // destructure below is positional, so inserting anywhere else would
        // silently shift every other dataset by one.
        companiesAPI.getAll().catch(() => [])
      ]);

      const [propsData, paymentsData, expensesData, compExpData, prospectsData, projectsData, calendarData, settingsData, tenantsData, companiesData] = results;

      setProperties(propsData.status === 'fulfilled' ? (propsData.value || []) : []);
      setPayments(paymentsData.status === 'fulfilled' ? (paymentsData.value || []) : []);
      setExpenses(expensesData.status === 'fulfilled' ? (expensesData.value || []) : []);
      setCompanyExpenses(compExpData.status === 'fulfilled' ? (compExpData.value || []) : []);
      setProspects(prospectsData.status === 'fulfilled' ? (prospectsData.value || []) : []);
      setProjects(projectsData.status === 'fulfilled' ? (projectsData.value || []) : []);
      setCalendarTasks(calendarData.status === 'fulfilled' ? (calendarData.value || []) : []);
      setSettings(settingsData.status === 'fulfilled' ? (settingsData.value || {}) : {});
      setTenants(tenantsData.status === 'fulfilled' ? (tenantsData.value || []) : []);
      // A failed companies fetch must never block the app — an org with no
      // companies is a perfectly valid state (personal-ownership only).
      setCompanies(companiesData.status === 'fulfilled' ? (companiesData.value || []) : []);

      // Log any failures for debugging
      results.forEach((r, i) => {
        if (r.status === 'rejected') {
          console.error(`Data load failed for item ${i}:`, r.reason);
        }
      });
    } catch (error) {
      console.error('Error loading data:', error);
      setDataError(error.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  // ===== COMPANY-003: derived company state =====
  // Every screen asks the same three questions — how many companies are
  // there, which one do I default to, and what is this company called.
  // Deriving them once here keeps the "single company = no extra clicks"
  // rule in one place instead of re-implemented on five screens.
  const activeCompanies = useMemo(
    () => (companies || []).filter(c => !(c.archived === true || c.archived === 'true')),
    [companies]
  );

  const isMultiCompany = activeCompanies.length > 1;

  // Only meaningful when there is exactly one company. With two or more we
  // deliberately return null so callers are forced to make the user choose
  // rather than quietly guessing — a wrong company is worse than a prompt.
  const defaultCompanyId = activeCompanies.length === 1 ? activeCompanies[0].id : null;

  const getCompanyName = useCallback((companyId) => {
    if (!companyId) return null;
    const c = (companies || []).find(x => String(x.id) === String(companyId));
    return c ? c.name : null;
  }, [companies]);

  // Resolves the company for any record. Payments and property expenses
  // deliberately have NO company_id of their own — they derive it from
  // their property, so reassigning a property to a different company moves
  // its whole transaction history with it and can never drift out of sync.
  const getCompanyIdForProperty = useCallback((propertyId) => {
    if (!propertyId) return null;
    const p = (properties || []).find(x => String(x.id) === String(propertyId));
    if (!p) return null;
    // Guard: a personally-owned property must never report a company, even
    // if a legacy row still carries a stale company_id from before the
    // COMPANY-003 server-side rule was enforced.
    if (String(p.ownership || '').toLowerCase() !== 'company') return null;
    return p.company_id || p.companyId || null;
  }, [properties]);

  return (
    <AppDataContext.Provider value={{
      properties, setProperties,
      payments, setPayments,
      expenses, setExpenses,
      companyExpenses, setCompanyExpenses,
      prospects, setProspects,
      projects, setProjects,
      calendarTasks, setCalendarTasks,
      tenants, setTenants,
      settings, setSettings,
      // COMPANY-003
      companies, setCompanies,
      activeCompanies, isMultiCompany, defaultCompanyId,
      getCompanyName, getCompanyIdForProperty,
      loading, dataError, loadAllData
    }}>
      {children}
    </AppDataContext.Provider>
  );
}

export function useAppData() {
  const context = useContext(AppDataContext);
  if (!context) throw new Error('useAppData must be used within AppDataProvider');
  return context;
}
